/* eslint-disable */
/**
 * ============================================================================
 *  THEME CONFIG — the only file a new company edits
 * ============================================================================
 *  Brand, colors, font size and spacing for the whole project.
 *
 *  Used by:
 *    - scripts/generate-css-vars.js → src/core/constants/variables.css (CSS + Tailwind)
 *    - src/core/constants/ConfigStyle.tsx → antd theme
 *
 *  After any change: restart `yarn local` / `npm run dev`.
 *
 *  Rules:
 *    - Every scale goes LIGHT (50 / 10) → DARK (900). Never reverse it.
 *    - Change a whole scale together, not one shade.
 *    - Shades: 50–100 backgrounds · 200–300 borders · 400–600 icons / hover
 *              700 main · 800–900 text
 *    - Text on a 50 / 100 background uses the 900 of the same scale.
 * ============================================================================
 */

/* ---------------------------------------------------------------------------
 * 1. BRAND
 * ------------------------------------------------------------------------- */
const brand = {
  name: 'Shell', // browser tab title (public/index.html) — set to your current <title>
  logo: 'images/PayPrompt-White.svg',
  logoCollapsed: 'images/paypromptLogoSmall.svg', // sidebar collapsed, auth error page
  loader: 'gifs/loader.gif', // page loader (src/assets/)
  loaderSmall: 'gifs/loading.gif', // small loader (table actions)
  favicon: '/favicon.ico', // set in public/index.html
  fontFamily: "'Onest', sans-serif", // all text (Onest loaded by @font-face in index.css)
  fontFamilySystem:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'", // modal, notification
};

/* ---------------------------------------------------------------------------
 * 2. PALETTE
 * ------------------------------------------------------------------------- */
const palette = {
  // PRIMARY PALETTE (#1434CB)
  'primary-50': '#E8EBFA',
  'primary-100': '#B6C0EF',
  'primary-200': '#93A2E7',
  'primary-300': '#6277DC',
  'primary-400': '#435DD5',
  'primary-500': '#1434CB',
  'primary-600': '#122FB9',
  'primary-700': '#0E2590',
  'primary-800': '#0B1D70',
  'primary-900': '#081655',

  // SECONDARY PALETTE (#FCC015)
  'secondary-50': '#FFF9E8',
  'secondary-100': '#FEEBB6',
  'secondary-200': '#FEE293',
  'secondary-300': '#FDD562',
  'secondary-400': '#FDCD44',
  'secondary-500': '#FCC015',
  'secondary-600': '#E5AF13',
  'secondary-700': '#B3880F',
  'secondary-800': '#8B6A0C',
  'secondary-900': '#6A5109',

  // Info
  'info-50': '#E8F1FF', // info bg
  'info-100': '#B7D5FE',
  'info-200': '#94C0FE',
  'info-300': '#63A3FD', // progress bar start
  'info-400': '#4591FD', // upload label hover
  'info-500': '#1676FC', // info icon / border, upload label
  'info-600': '#146BE5',
  'info-700': '#1054B3', // profile avatar bg
  'info-800': '#0C418B',
  'info-900': '#09326A', // text on info-50

  // Success
  'success-50': '#EEF9E8', // success bg
  'success-100': '#C9EDB8', // finished step bg
  'success-200': '#AFE496',
  'success-300': '#8BD766', // finished step line
  'success-400': '#75D048',
  'success-500': '#52C41A', // success icon / border
  'success-600': '#4BB218',
  'success-700': '#3A8B12',
  'success-800': '#2D6C0E', // finished step border / number
  'success-900': '#22520B', // text on success-50

  // Danger
  'danger-50': '#FDEDED', // error bg
  'danger-100': '#F8C6C6', // error box border
  'danger-200': '#F5AAAB', // error row, delete hover
  'danger-300': '#F18484', // error row hover
  'danger-400': '#EE6C6D',
  'danger-500': '#EA4748', // error text / border / icon, required *
  'danger-600': '#D54142',
  'danger-700': '#A63233',
  'danger-800': '#812728', // error step border / number
  'danger-900': '#621E1E', // text on danger-50

  // Neutral: 10–40 backgrounds · 50–90 borders · 100–300 muted text · 400–900 text
  'neutral-10': '#FFFFFF', // card, input, modal, sider, header; text on brand
  'neutral-20': '#FAFAFA', // table odd row, option, preview box
  'neutral-30': '#F4F4F4', // page bg, table header, row hover
  'neutral-40': '#EDEDED', // disabled bg, table inner lines
  'neutral-50': '#E1E1E1', // light border
  'neutral-60': '#D2D2D2', // default border
  'neutral-70': '#C3C3C3', // divider
  'neutral-80': '#B5B5B5', // disabled date text
  'neutral-90': '#A6A6A6',
  'neutral-100': '#959595', // disabled text, header text
  'neutral-200': '#888888', // secondary text, inactive step, search icon
  'neutral-300': '#6B6B6B', // placeholder, table header text
  'neutral-400': '#4D4D4D', // secondary text
  'neutral-500': '#3E3E3E', // body text, sider menu text
  'neutral-600': '#2E2E2E', // titles
  'neutral-700': '#1F1F1F', // main text, labels, table cells
  'neutral-800': '#171717', // typed text in inputs
  'neutral-900': '#0F0F0F', // headings, modal / page header title

  // One-off
  black: '#000000',
  greenTopIcon: '#059669', // dashboard Incentive Program card
  greenRightIcon: '#34D399',
  pinkTopIcon: '#EC4899', // dashboard Role card
  pinkRightIcon: '#F472B6',
  blueTopIcon: '#2563EB', // dashboard User card
  blueRightIcon: '#60A5FA',
};

/* ---------------------------------------------------------------------------
 * 3. FONT SIZE  →  --font-<key>, Tailwind text-<key>
 * ------------------------------------------------------------------------- */
const fontSize = {
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  base: '1rem', // 16px
  md: '1.25rem', // 20px
  lg: '1.625rem', // 26px
  xl: '2rem', // 32px
  '2xl': '2.625rem', // 42px
};

/* ---------------------------------------------------------------------------
 * 4. SPACING  →  --spacing-<key>, Tailwind p-md, gap-sm …
 * ------------------------------------------------------------------------- */
const spacing = {
  xs: '0.25rem', // 4px
  sm: '0.5rem', // 8px
  md: '1rem', // 16px
  lg: '1.5rem', // 24px
  xl: '2rem', // 32px
  '2xl': '2.5rem', // 40px
  '3xl': '3rem', // 48px
  '4xl': '3.5rem', // 56px
};

module.exports = {
  brand,
  palette,
  fontSize,
  spacing,
};
